"use client";

import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const eigeneFlaecheVorn =
  "pointer-coarse:before:absolute pointer-coarse:before:inset-0 pointer-coarse:before:z-[1] pointer-coarse:before:pointer-events-auto pointer-coarse:disabled:z-[1]";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius)] text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // `primary`, nicht `foreground`: die monochrome Optik gehoert ins Theme,
        // nicht in den Code. Mit `bg-foreground` sah der Standard-Button in JEDEM
        // Theme schwarz/weiss aus — die Akzentfarbe eines Themes erschien nie.
        // Im Janeway-Theme ist primary 9 % (hell) bzw. 98 % (dunkel) und damit
        // praktisch deckungsgleich mit foreground; dort aendert sich nichts.
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-7 px-3 text-xs",
        lg: "h-11 px-6 text-base",
        // M-097: am groben Zeiger ragt ein unsichtbares ::after bis auf 44 px hinaus;
        // Optik und Layout am Schreibtisch bleiben. Das ::after malt ueber den
        // FRUEHEREN Nachbarn — deshalb deckt ein ::before mit z-[1] den eigenen Knopf
        // ab, sonst loest dessen Rand den Nachbarn aus. Deaktiviert macht opacity-50
        // den Knopf zum Stapelkontext, der das ::before einsperrt — daher dort z-[1]
        // (Waechter knopf-trefferflaeche-am-handy).
        icon: `relative h-9 w-9 pointer-coarse:after:absolute pointer-coarse:after:-inset-1 ${eigeneFlaecheVorn}`,
        "icon-sm": `relative h-7 w-7 pointer-coarse:after:absolute pointer-coarse:after:-inset-2 ${eigeneFlaecheVorn}`,
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as child element (e.g. wrapping a Link). */
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants, type ButtonProps };
